<!--
  The editor for a dict-type entity
  (currently, only "dict" and "native" types exist)
  Used by EntityPage
-->

<template>
  <el-form-sub
    :prop="prop"
    :model="{ options }">
    <comparator :view="options" :compare="otherOptions" prop="value"
      :required="true"
      :rules="BaseTypeValidator">
      <template v-slot="{ value, other, index, view, compare, attrs, status }">
        <div>
          <!-- input for the value of each option -->
          <el-form-errors>
            <el-button-group>
              <el-form-item-code-input :ref="view && 'value-' + index"
                :class="'comparable-' + status"
                v-model="value.value"
                type="json"
                :prop="view && 'options.' + index + '.value'"
                v-bind="attrs"
                :show-message="true"
                field="value"
                :varprefix="{}"
                :varstatus="{}"
                placeholder="option value"
                :disabled="compare"></el-form-item-code-input>
              <el-button
                icon="el-icon-delete"
                @click="view && deleteValue(index)"
                :disabled="compare"
                :tabindex="-1"></el-button>
            </el-button-group>
          </el-form-errors>
          <fieldset>
            <legend>Options</legend>
            <!-- strings that will be identified as the value -->
            <el-row>
              <comparator prop
                :view="value.options" :compare="other && other.options"
                :required="true"
                :rules="[ReadableValidator, duplicateValidator]">
                <template v-slot="{ value: v, index: i, view, compare, attrs, status }">
                  <el-col :span="12" style="padding: 5px">
                    <el-form-small-item :class="'comparable-' + status"
                      :prop="view && 'options.' + index + '.options.' + i"
                      v-bind="attrs">
                      <el-button-group>
                        <el-input :ref="view && 'options-' + index + '-' + i"
                          :value="v"
                          @input="v => view && $set(value.options, i, v)"
                          :disabled="compare"></el-input>
                        <el-button
                          icon="el-icon-delete"
                          @click="view && deleteOption(index, i)"
                          :disabled="compare"
                          :tabindex="-1"></el-button>
                      </el-button-group>
                    </el-form-small-item>
                  </el-col>
                </template>
              </comparator>
              <!-- input for a new string -->
              <el-col v-if="view && !$route.query.view"
                :span="12" style="padding: 5px">
                <el-form-small-item
                  :prop="'options.' + index + '.options'"
                  :rules="EmptyOptionsValidator">
                  <el-button-group>
                    <el-input :ref="view && 'options-' + index + '-new'"
                      v-model="value.newOption"
                      @input="handleNewOption(index)"></el-input>
                    <el-button
                      icon="el-icon-circle-plus-outline"
                      :disabled="true"
                      :tabindex="-1"></el-button>
                  </el-button-group>
                </el-form-small-item>
              </el-col>
            </el-row>
          </fieldset>
        </div>
      </template>
    </comparator>
    <!-- input for a new value -->
    <comparator-filter>
      <template v-slot="{ view }">
        <div>
          <el-form-small-item v-if="view"
            prop="options"
            :rules="EmptyValuesValidator">
            <el-form ref="newForm"
              :model="newValue"
              @submit.native.prevent="handleNewValue">
              <el-form-errors>
                <el-button-group>
                  <el-form-item-code-input
                    v-model="newValue.value"
                    type="json"
                    :prop="'value'"
                    :rules="BaseTypeValidator"
                    :show-message="true"
                    field="value"
                    :varprefix="{}"
                    :varstatus="{}"
                    placeholder="add a value"></el-form-item-code-input>
                  <el-button
                    native-type="submit"
                    icon="el-icon-circle-plus-outline"
                    :disabled="newValue.value === undefined"
                    :tabindex="-1"></el-button>
                </el-button-group>
              </el-form-errors>
            </el-form>
          </el-form-small-item>
        </div>
      </template>
    </comparator-filter>
  </el-form-sub>
</template>

<script>
import { ReadableValidator } from '../../utils/validators'
import ElFormItemCodeInput from '../element/ElFormItemCodeInput.vue'
import deeEqual from 'fast-deep-equal'

// check that entity is not empty
const EmptyValuesValidator = {
  validator (rule, value, callback) {
    callback(
      !value.length ? new Error('value required') : undefined)
  },
  trigger: 'submit',
}
// check that the options of each value are not empty
const EmptyOptionsValidator = {
  validator (rule, value, callback) {
    callback(
      !value.length ? new Error('option required') : undefined)
  },
  trigger: 'submit',
}
// check that the value is a base type
const BaseTypeValidator = {
  validator (rule, value, callback) {
    callback(typeof value === 'object'
      ? new Error('only base types allowed') : undefined)
  },
  trigger: 'submit',
}

export default {
  name: 'EntityDictEditor',
  components: {
    ElFormItemCodeInput,
  },
  props: {
    value: Object,
    other: Object,
    prop: String,
  },
  data () {
    // check for duplicate options
    let duplicateValidator = (rule, value, callback) => callback(
      this.counts[value] > 1 ? new Error('duplicate option') : undefined)
    return {
      // an array of value-options objects, with key and new option
      options: [],
      copy: {}, // a copy of value, to be emitted
      newValue: { value: undefined }, // the model to create a new value
      counts: {}, // how many times is used each option, error if > 1
      ReadableValidator,
      EmptyValuesValidator,
      EmptyOptionsValidator,
      BaseTypeValidator,
      duplicateValidator: {
        validator: duplicateValidator,
        trigger: 'blur',
      },
    }
  },
  computed: {
    // same as options, but from other
    otherOptions () {
      if (!this.other) return this.other
      let optionsd = {}
      let optionsl = []
      for (let opt in this.other) {
        let value = this.other[opt]
        if (!(value in optionsd)) {
          optionsd[value] = []
          optionsl.push(value)
        }
        optionsd[value].push(opt)
      }
      optionsl.sort()
      let out = []
      for (let opt of optionsl) {
        out.push({
          value: opt,
          options: optionsd[opt],
          key: Math.random() + '',
        })
      }
      return out
    },
  },
  // init copy and options
  created () {
    this.valueUpdated()
  },
  watch: {
    // value has changed, update copy and options
    value () { this.valueUpdated() },
    // options has changed, update copy and counts
    options: {
      handler () {
        let options = {}
        this.counts = {}
        for (let opt of this.options) {
          for (let o of opt.options) {
            options[o] = opt.value
            this.counts[o] = (this.counts[o] || 0) + 1
          }
        }
        this.copy = options
      },
      deep: true,
    },
    // copy has changed, emit it
    copy () {
      if (deeEqual(this.value, this.copy)) return
      this.$emit('input', this.copy)
    },
    // automatocally create values
    'newValue.value': 'handleNewValue',
  },
  methods: {
    // updates options and copy according to value
    valueUpdated () {
      if (deeEqual(this.value, this.copy)) return
      this.newValue.value = undefined // clear newValue
      let optionsd = {} // a {value: [options]} dict
      let optionsl = [] // a list of value, for sorting
      for (let opt in this.value) {
        let value = this.value[opt]
        if (!(value in optionsd)) {
          optionsd[value] = []
          optionsl.push(value)
        }
        optionsd[value].push(opt)
      }
      optionsl.sort()
      this.options = [] // build options
      for (let opt of optionsl) {
        this.options.push({
          value: opt,
          options: optionsd[opt],
          newOption: '',
          key: Math.random() + '',
        })
      }
    },
    // create a new value
    // handleNewValue () {
    //   // undefined means empty of invalid value
    //   if (this.newValue.value === undefined) return
    //   // validate the form
    //   this.$refs.newForm.validate(valid => {
    //     if (!valid) return
    //     // add the new value
    //     this.options.push({
    //       value: this.newValue.value,
    //       options: [],
    //       newOption: '',
    //       key: Math.random() + '',
    //     })
    //     // clear newValue
    //     this.newValue.value = undefined
    //   })
    //   // give focus to the new option of the create value
    //   this.$nextTick(() => {
    //     this.$refs['options-' + (this.options.length - 1) + '-new'].focus()
    //   })
    //   this.$nextTick(() => {
    //     this.$refs['value-' + (this.options.length - 1)].focus()
    //   })
    // },
    handleNewValue () {
      // undefined means empty of invalid value
      if (this.newValue.value === undefined) return
      this.options.push({
        value: this.newValue.value,
        options: [],
        newOption: '',
        key: Math.random() + '',
      })
      // clear newValue
      // this.newValue.value = undefined
      // give focus to the new option of the create value
      this.$nextTick(() => {
        this.newValue.value = undefined
        this.$nextTick(() => {
          this.$refs['value-' + (this.options.length - 1)].focus()
        })
      })
    },
    // create a new option
    handleNewOption (optindex) {
      let option = this.options[optindex]
      if (!option.newOption) return
      option.options.push(option.newOption)
      // next characters will be typed in the newOption input
      // thus, once the created option appears
      // give it back the value of newOption, and the focus
      this.$nextTick(() => {
        option.options[option.options.length - 1] = option.newOption
        this.$refs['options-' + optindex + '-' +
          (option.options.length - 1)].focus()
        option.newOption = ''
      })
    },
    // delete a value
    deleteValue (optindex) {
      this.options.splice(optindex, 1)
    },
    // delete an option
    deleteOption (optindex, index) {
      this.options[optindex].options.splice(index, 1)
    },
  },
}
</script>
